<template>
	<VerticalLayout>
		<Select
			class="width-100"
			v-model:value="selectedAddresses"
			:options="addresses"
			mode="multiple"
			:allowClear="true"
			:placeholder="$t('userAddrSessFilter.addressFilter')"
			@change="onChangeLocal"
			:getPopupContainer="getPopupContainer"
			show-search
		/>
		<Select
			v-if="showUsernameFilter"
			class="width-100"
			v-model:value="selectedUsernames"
			:options="usernames"
			mode="multiple"
			:allowClear="true"
			:placeholder="$t('userAddrSessFilter.usernameFilter')"
			@change="onChangeLocal"
			:getPopupContainer="getPopupContainer"
			show-search
		/>
		<Select
			class="width-100"
			v-model:value="selectedSessionIds"
			:options="sessionIds"
			mode="multiple"
			:allowClear="true"
			:placeholder="$t('userAddrSessFilter.sessionIdFilter')"
			@change="onChangeLocal"
			:getPopupContainer="getPopupContainer"
			show-search
		/>
	</VerticalLayout>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { Select } from 'ant-design-vue';

import Utils from '@/api/Utils';

import { usePopupContainer } from '@/utils/usePopupContainer';
import { useRoversHistoryStore } from '@/stores/roversHistoryStore';
import { useUserStore } from '@/stores/user';

import VerticalLayout from '@/components/common/VerticalLayout';

export default {
	components: { Select, VerticalLayout },
	props: {
		addressField: {
			type: String,
			required: true,
		},
		usernameField: {
			type: String,
			required: true,
		},
		sessionIdField: {
			type: String,
			required: true,
		},
		onChange: {
			type: Function,
			required: false,
			default: () => {},
		},
	},
	setup(props) {
		const { getPopupContainer } = usePopupContainer();
		const roversHistoryStore = useRoversHistoryStore();
		const userStore = useUserStore();
		const showUsernameFilter = computed(() => userStore.role != 'rover');

		const addresses = ref([]);
		const usernames = ref([]);
		const sessionIds = ref([]);

		const selectedAddresses = computed({
			get: () => roversHistoryStore.filterSelectedAddresses,
			set: value => roversHistoryStore.setFilterState({ addresses: value }),
		});

		const selectedUsernames = computed({
			get: () => roversHistoryStore.filterSelectedUsernames,
			set: value => roversHistoryStore.setFilterState({ usernames: value }),
		});

		const selectedSessionIds = computed({
			get: () => roversHistoryStore.filterSelectedSessionIds,
			set: value => roversHistoryStore.setFilterState({ sessionIds: value }),
		});

		const updateOptions = () => {
			const addressSet = new Set();
			const usernameSet = new Set();
			const sessionIdSet = new Set();
			for (const item of roversHistoryStore.sessions) {
				const sessionId = item[props.sessionIdField];
				if (sessionId != null && sessionId !== '') {
					sessionIdSet.add(sessionId);
				}
				const username = item[props.usernameField];
				if (username != null && username !== '') {
					usernameSet.add(username);
				}
				const address = item[props.addressField];
				if (address != null && address !== '') {
					addressSet.add(address);
				}
			}
			addresses.value = Utils.sortArray(Array.from(addressSet).map(item => ({
				value: item,
				label: item,
			})), Utils.objectFieldMultiSorter('value'));
			sessionIds.value = Utils.sortArray(Array.from(sessionIdSet).map(item => ({
				value: item,
				label: item,
			})), Utils.objectFieldMultiSorter('value'));
			usernames.value = Utils.sortArray(Array.from(usernameSet).map(item => ({
				value: item,
				label: item,
			})), Utils.objectFieldMultiSorter('value'));

			roversHistoryStore.setFilterState({
				addresses: selectedAddresses.value.filter(v => addresses.value.some(opt => opt.value === v)),
				usernames: showUsernameFilter.value ? selectedUsernames.value.filter(v => usernames.value.some(opt => opt.value === v)) : [],
				sessionIds: selectedSessionIds.value.filter(v => sessionIds.value.some(opt => opt.value === v)),
			});
		};

		watch(() => roversHistoryStore.sessions, () => {
			updateOptions();
		}, { immediate: true });

		const onChangeLocal = () => {
			roversHistoryStore.onFilterChange();
			props.onChange();
		};

		return {
			addresses,
			selectedAddresses,
			usernames,
			selectedUsernames,
			sessionIds,
			selectedSessionIds,
			getPopupContainer,
			onChangeLocal,
			showUsernameFilter,
		};
	},
};
</script>
